import { useState, useEffect, ReactNode } from 'react';
import { trpc } from '../lib/trpc';
import { useProject } from '../context/ProjectContext';

// ===========================================
// KEYWORD HIGHLIGHTING
// ===========================================

/**
 * Highlights keywords within text by wrapping matches in styled spans.
 *
 * @param text - The text content to search within
 * @param keywords - Array of keywords to highlight
 * @returns ReactNode with highlighted keywords wrapped in spans
 */
function highlightKeywords(text: string | null | undefined, keywords: string[] | null | undefined): ReactNode {
  if (!text) return null;
  if (!keywords || keywords.length === 0) return text;

  // Escape special regex characters in keywords and sort by length (longest first)
  const sortedKeywords = [...keywords].sort((a, b) => b.length - a.length);
  const escapedKeywords = sortedKeywords.map(kw =>
    kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  );

  // Create regex pattern matching any keyword (case-insensitive)
  const pattern = new RegExp(`(${escapedKeywords.join('|')})`, 'gi');

  // Split text by matches
  const parts = text.split(pattern);

  if (parts.length === 1) return text;

  return parts.map((part, index) => {
    // Check if this part matches any keyword (case-insensitive)
    const isMatch = keywords.some(
      kw => part.toLowerCase() === kw.toLowerCase()
    );

    if (isMatch) {
      return (
        <span
          key={index}
          style={{
            backgroundColor: 'rgba(180, 80, 70, 0.3)',
            color: '#A04040',
            padding: '1px 4px',
            borderRadius: '3px',
            fontWeight: '600',
          }}
        >
          {part}
        </span>
      );
    }

    return part;
  });
}

// ===========================================
// ALERT DETAIL COMPONENT
// ===========================================

interface ContextMessage {
  id: string;
  content: string;
  direction: string;
  createdAt: string | null;
  messageType: string;
}

interface SafetyAlertNote {
  author: string;
  authorId: string;
  time: string;
  text: string;
}

interface AlertDetailProps {
  alertId: string;
  onClose: () => void;
  onResolve: (alertId: string) => void;
  isResolving?: boolean;
}

/**
 * AlertDetail component displays the full context of a safety alert.
 *
 * Shows:
 * - 3 messages before the flagged message
 * - The flagged message with keyword highlighting
 * - 3 messages after the flagged message
 * - Notes thread for collaboration
 */
function AlertDetail({ alertId, onClose, onResolve, isResolving }: AlertDetailProps) {
  const [newNoteText, setNewNoteText] = useState('');
  const utils = trpc.useUtils();

  const contextQuery = trpc.safety.getAlertContext.useQuery(
    { id: alertId },
    { enabled: !!alertId }
  );

  const alertQuery = trpc.safety.get.useQuery(
    { id: alertId },
    { enabled: !!alertId }
  );

  const addNoteMutation = trpc.safety.addNote.useMutation({
    onSuccess: () => {
      setNewNoteText('');
      utils.safety.get.invalidate({ id: alertId });
      utils.safety.alerts.invalidate();
    },
  });

  const handleAddNote = () => {
    if (!newNoteText.trim()) return;
    addNoteMutation.mutate({
      alertId,
      text: newNoteText.trim(),
    });
  };

  const renderMessage = (msg: ContextMessage, isFlagged: boolean) => {
    const isInbound = msg.direction === 'inbound';
    const content = isFlagged && contextQuery.data?.keywordsMatched
      ? highlightKeywords(msg.content, contextQuery.data.keywordsMatched as string[])
      : msg.content;

    return (
      <div
        key={msg.id}
        style={{
          ...detailStyles.messageRow,
          justifyContent: isInbound ? 'flex-start' : 'flex-end',
        }}
      >
        <div
          style={{
            ...detailStyles.messageBubble,
            backgroundColor: isFlagged
              ? 'rgba(180, 80, 70, 0.1)'
              : isInbound
                ? '#f1f5f9'
                : '#e0f2fe',
            border: isFlagged ? '2px solid rgba(180, 80, 70, 0.4)' : 'none',
            maxWidth: '80%',
          }}
        >
          <div style={detailStyles.messageHeader}>
            <span style={detailStyles.messageDirection}>
              {isInbound ? '👤 User' : '🤖 Bot'}
            </span>
            <span style={detailStyles.messageTime}>
              {msg.createdAt ? new Date(msg.createdAt).toLocaleString() : 'Unknown time'}
            </span>
          </div>
          <div style={detailStyles.messageContent}>{content}</div>
          {isFlagged && (
            <div style={detailStyles.flaggedBadge}>
              ⚠️ Flagged Message
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={detailStyles.overlay} onClick={onClose}>
      <div style={detailStyles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={detailStyles.header}>
          <h2 style={detailStyles.title}>Alert Details</h2>
          <button style={detailStyles.closeButton} onClick={onClose}>
            ✕
          </button>
        </div>

        {contextQuery.isLoading || alertQuery.isLoading ? (
          <div style={detailStyles.loading}>Loading context...</div>
        ) : contextQuery.error || alertQuery.error ? (
          <div style={detailStyles.error}>
            Error loading alert details: {contextQuery.error?.message || alertQuery.error?.message}
          </div>
        ) : (
          <>
            {/* Alert Info */}
            {alertQuery.data && (
              <div style={detailStyles.alertInfo}>
                <div style={detailStyles.alertInfoRow}>
                  <span style={detailStyles.alertInfoLabel}>User:</span>
                  <span style={detailStyles.alertInfoValue}>
                    {alertQuery.data.user?.name || 'Unknown User'}
                  </span>
                </div>
                <div style={detailStyles.alertInfoRow}>
                  <span style={detailStyles.alertInfoLabel}>Severity:</span>
                  <span
                    style={{
                      ...detailStyles.severityBadge,
                      backgroundColor: severityColors[alertQuery.data.severity]?.bg || severityColors.low.bg,
                      color: severityColors[alertQuery.data.severity]?.text || severityColors.low.text,
                    }}
                  >
                    {alertQuery.data.severity.toUpperCase()}
                  </span>
                </div>
                {contextQuery.data?.keywordsMatched && (contextQuery.data.keywordsMatched as string[]).length > 0 && (
                  <div style={detailStyles.alertInfoRow}>
                    <span style={detailStyles.alertInfoLabel}>Triggered by:</span>
                    <div style={detailStyles.keywordsList}>
                      {(contextQuery.data.keywordsMatched as string[]).map((kw, idx) => (
                        <span key={idx} style={detailStyles.keywordBadge}>
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Context Messages */}
            <div style={detailStyles.contextSection}>
              <h3 style={detailStyles.contextTitle}>Conversation Context</h3>

              {/* Before messages */}
              {contextQuery.data?.before && contextQuery.data.before.length > 0 && (
                <div style={detailStyles.contextGroup}>
                  <div style={detailStyles.contextDivider}>
                    <span style={detailStyles.contextLabel}>
                      {contextQuery.data.before.length} message{contextQuery.data.before.length !== 1 ? 's' : ''} before
                    </span>
                  </div>
                  {contextQuery.data.before.map((msg) => renderMessage(msg as ContextMessage, false))}
                </div>
              )}

              {/* Flagged message */}
              {contextQuery.data?.flagged && (
                <div style={detailStyles.contextGroup}>
                  {renderMessage(contextQuery.data.flagged as ContextMessage, true)}
                </div>
              )}

              {/* After messages */}
              {contextQuery.data?.after && contextQuery.data.after.length > 0 && (
                <div style={detailStyles.contextGroup}>
                  <div style={detailStyles.contextDivider}>
                    <span style={detailStyles.contextLabel}>
                      {contextQuery.data.after.length} message{contextQuery.data.after.length !== 1 ? 's' : ''} after
                    </span>
                  </div>
                  {contextQuery.data.after.map((msg) => renderMessage(msg as ContextMessage, false))}
                </div>
              )}

              {/* No context available */}
              {(!contextQuery.data?.before || contextQuery.data.before.length === 0) &&
                !contextQuery.data?.flagged &&
                (!contextQuery.data?.after || contextQuery.data.after.length === 0) && (
                <div style={detailStyles.noContext}>
                  No conversation context available for this alert.
                </div>
              )}
            </div>

            {/* Notes Thread Section */}
            <div style={detailStyles.notesSection}>
              <h3 style={detailStyles.notesTitle}>
                Resolution Notes
                {alertQuery.data?.notes && (alertQuery.data.notes as SafetyAlertNote[]).length > 0 && (
                  <span style={detailStyles.notesCount}>
                    ({(alertQuery.data.notes as SafetyAlertNote[]).length})
                  </span>
                )}
              </h3>

              {/* Existing Notes Thread */}
              <div style={detailStyles.notesThread}>
                {alertQuery.data?.notes && (alertQuery.data.notes as SafetyAlertNote[]).length > 0 ? (
                  (alertQuery.data.notes as SafetyAlertNote[]).map((note, index) => (
                    <div key={index} style={detailStyles.noteItem}>
                      <div style={detailStyles.noteHeader}>
                        <span style={detailStyles.noteAuthor}>{note.author}</span>
                        <span style={detailStyles.noteTime}>
                          {new Date(note.time).toLocaleString()}
                        </span>
                      </div>
                      <div style={detailStyles.noteText}>{note.text}</div>
                    </div>
                  ))
                ) : (
                  <div style={detailStyles.noNotes}>
                    No notes yet. Add a note to start the resolution thread.
                  </div>
                )}
              </div>

              {/* Add Note Form */}
              <div style={detailStyles.addNoteForm}>
                <textarea
                  value={newNoteText}
                  onChange={(e) => setNewNoteText(e.target.value)}
                  placeholder="Add a note about this alert..."
                  style={detailStyles.noteTextarea}
                  rows={3}
                  disabled={addNoteMutation.isPending}
                />
                <div style={detailStyles.addNoteActions}>
                  <button
                    onClick={handleAddNote}
                    disabled={!newNoteText.trim() || addNoteMutation.isPending}
                    style={{
                      ...detailStyles.addNoteButton,
                      opacity: !newNoteText.trim() || addNoteMutation.isPending ? 0.5 : 1,
                    }}
                  >
                    {addNoteMutation.isPending ? 'Adding...' : 'Add Note'}
                  </button>
                  {addNoteMutation.error && (
                    <span style={detailStyles.noteError}>
                      Error: {addNoteMutation.error.message}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            {alertQuery.data && (alertQuery.data.status === 'needs_review' || alertQuery.data.status === 'in_progress') && (
              <div style={detailStyles.actions}>
                <button
                  style={detailStyles.resolveButton}
                  onClick={() => onResolve(alertId)}
                  disabled={isResolving}
                >
                  ✓ {isResolving ? 'Resolving...' : 'Mark as Resolved'}
                </button>
                <button style={detailStyles.cancelButton} onClick={onClose}>
                  Cancel
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const detailStyles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '16px',
    width: '90%',
    maxWidth: '700px',
    maxHeight: '85vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid rgba(93, 86, 78, 0.1)',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    color: '#3D3A36',
  },
  closeButton: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    border: 'none',
    background: 'rgba(93, 86, 78, 0.08)',
    fontSize: '16px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#5C564E',
  },
  loading: {
    padding: '40px',
    textAlign: 'center',
    color: '#64748b',
  },
  error: {
    margin: '24px',
    padding: '16px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
  },
  alertInfo: {
    padding: '20px 24px',
    backgroundColor: 'rgba(93, 86, 78, 0.03)',
    borderBottom: '1px solid rgba(93, 86, 78, 0.1)',
  },
  alertInfoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '8px',
  },
  alertInfoLabel: {
    fontSize: '13px',
    color: '#9B9285',
    minWidth: '80px',
  },
  alertInfoValue: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#3D3A36',
  },
  severityBadge: {
    padding: '3px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '600',
  },
  keywordsList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  keywordBadge: {
    padding: '3px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '600',
    backgroundColor: 'rgba(180, 80, 70, 0.15)',
    color: '#A04040',
  },
  contextSection: {
    padding: '20px 24px',
  },
  contextTitle: {
    margin: '0 0 16px 0',
    fontSize: '14px',
    fontWeight: '600',
    color: '#3D3A36',
  },
  contextGroup: {
    marginBottom: '12px',
  },
  contextDivider: {
    display: 'flex',
    alignItems: 'center',
    margin: '16px 0 12px 0',
  },
  contextLabel: {
    fontSize: '11px',
    color: '#9B9285',
    backgroundColor: 'white',
    padding: '0 8px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  messageRow: {
    display: 'flex',
    marginBottom: '8px',
  },
  messageBubble: {
    padding: '12px 16px',
    borderRadius: '12px',
  },
  messageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '6px',
  },
  messageDirection: {
    fontSize: '11px',
    fontWeight: '600',
    color: '#5C564E',
  },
  messageTime: {
    fontSize: '10px',
    color: '#9B9285',
  },
  messageContent: {
    fontSize: '13px',
    color: '#3D3A36',
    lineHeight: '1.5',
  },
  flaggedBadge: {
    marginTop: '8px',
    fontSize: '11px',
    fontWeight: '600',
    color: '#A04040',
  },
  noContext: {
    padding: '40px 20px',
    textAlign: 'center',
    color: '#9B9285',
    fontSize: '14px',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    padding: '20px 24px',
    borderTop: '1px solid rgba(93, 86, 78, 0.1)',
  },
  resolveButton: {
    flex: 1,
    padding: '12px 20px',
    borderRadius: '8px',
    background: 'rgba(152, 184, 154, 0.15)',
    border: '1px solid rgba(152, 184, 154, 0.3)',
    fontSize: '14px',
    fontWeight: '600',
    color: '#5A8A5E',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  cancelButton: {
    flex: 1,
    padding: '12px 20px',
    borderRadius: '8px',
    background: 'white',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    fontSize: '14px',
    fontWeight: '500',
    color: '#5C564E',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  // Notes Section Styles
  notesSection: {
    padding: '20px 24px',
    borderTop: '1px solid rgba(93, 86, 78, 0.1)',
  },
  notesTitle: {
    margin: '0 0 16px 0',
    fontSize: '14px',
    fontWeight: '600',
    color: '#3D3A36',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  notesCount: {
    fontSize: '12px',
    fontWeight: '500',
    color: '#9B9285',
  },
  notesThread: {
    marginBottom: '16px',
    maxHeight: '250px',
    overflowY: 'auto',
  },
  noteItem: {
    padding: '12px 16px',
    borderRadius: '8px',
    backgroundColor: 'rgba(93, 86, 78, 0.04)',
    marginBottom: '8px',
  },
  noteHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '6px',
  },
  noteAuthor: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#3D3A36',
  },
  noteTime: {
    fontSize: '11px',
    color: '#9B9285',
  },
  noteText: {
    fontSize: '13px',
    color: '#5C564E',
    lineHeight: '1.5',
    whiteSpace: 'pre-wrap',
  },
  noNotes: {
    padding: '20px',
    textAlign: 'center',
    color: '#9B9285',
    fontSize: '13px',
    backgroundColor: 'rgba(93, 86, 78, 0.03)',
    borderRadius: '8px',
  },
  addNoteForm: {
    marginTop: '12px',
  },
  noteTextarea: {
    width: '100%',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    fontSize: '13px',
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: '80px',
    boxSizing: 'border-box',
  },
  addNoteActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginTop: '8px',
  },
  addNoteButton: {
    padding: '10px 20px',
    borderRadius: '8px',
    background: '#3D3A36',
    border: 'none',
    fontSize: '13px',
    fontWeight: '600',
    color: 'white',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  noteError: {
    fontSize: '12px',
    color: '#A04040',
  },
};

// Status type from the API
type AlertStatus = 'needs_review' | 'in_progress' | 'resolved' | 'false_positive';
type AlertSeverity = 'high' | 'medium' | 'low' | 'critical';
type TabType = 'alerts' | 'keywords' | 'protocols';

// Color configurations matching the prototype
const severityColors: Record<string, { bg: string; text: string; dot: string; border: string }> = {
  critical: { bg: 'rgba(180, 80, 70, 0.15)', text: '#A04040', dot: '#A04040', border: 'rgba(180, 80, 70, 0.3)' },
  high: { bg: 'rgba(201, 140, 130, 0.15)', text: '#8B5A50', dot: '#8B5A50', border: 'rgba(201, 140, 130, 0.3)' },
  medium: { bg: 'rgba(232, 200, 140, 0.15)', text: '#9B7B3C', dot: '#9B7B3C', border: 'rgba(232, 200, 140, 0.3)' },
  low: { bg: 'rgba(152, 184, 154, 0.15)', text: '#5A8A5E', dot: '#5A8A5E', border: 'rgba(152, 184, 154, 0.3)' },
};

const statusColors: Record<string, { bg: string; text: string; label: string }> = {
  needs_review: { bg: 'rgba(180, 80, 70, 0.15)', text: '#A04040', label: 'Needs Review' },
  in_progress: { bg: 'rgba(232, 200, 140, 0.15)', text: '#9B7B3C', label: 'In Progress' },
  resolved: { bg: 'rgba(152, 184, 154, 0.15)', text: '#5A8A5E', label: 'Resolved' },
  false_positive: { bg: 'rgba(93, 86, 78, 0.1)', text: '#706A62', label: 'False Positive' },
};

const categoryIcons: Record<string, string> = {
  'self-harm': '🚨',
  'distress': '😢',
  'frustration': '😤',
  'negative_sentiment': '📉',
  'crisis': '⚠️',
  'default': '⚠️',
};

// Keyword category configuration (excluding 'critical' which maps to 'high' in the API)
// The UI shows 'critical' but we map it to 'high' when saving to the bot configuration
interface KeywordCategory {
  name: string;
  severity: AlertSeverity;
  dbField: 'overrideSafetyKeywordsHigh' | 'overrideSafetyKeywordsMedium' | 'overrideSafetyKeywordsLow';
  keywords: string[];
  protocol: string;
}

// Default keyword categories for display when no bot data is loaded
const defaultKeywordCategories: KeywordCategory[] = [
  {
    name: 'Crisis / Self-Harm',
    severity: 'high' as AlertSeverity,
    dbField: 'overrideSafetyKeywordsHigh',
    keywords: [],
    protocol: 'crisis_immediate',
  },
  {
    name: 'Moderate Distress',
    severity: 'medium' as AlertSeverity,
    dbField: 'overrideSafetyKeywordsMedium',
    keywords: [],
    protocol: 'frustration_standard',
  },
  {
    name: 'Negative Sentiment',
    severity: 'low' as AlertSeverity,
    dbField: 'overrideSafetyKeywordsLow',
    keywords: [],
    protocol: 'monitor_only',
  },
];

// Mocked protocols for the Protocols tab
const protocols = [
  { id: 'crisis_immediate', name: 'Crisis - Immediate', description: 'AI pauses, crisis resources sent, support notified immediately', color: '#A04040' },
  { id: 'distress_high', name: 'Distress - High', description: 'Empathetic response, resources offered, flagged for review', color: '#8B5A50' },
  { id: 'frustration_standard', name: 'Frustration - Standard', description: 'Supportive response, continue conversation', color: '#9B7B3C' },
  { id: 'monitor_only', name: 'Monitor Only', description: 'Log and monitor, no immediate action', color: '#5A8A5E' },
];

/**
 * Safety page component.
 *
 * Displays safety alerts with filtering and resolution capabilities.
 * Features tabbed interface with Alert Queue, Keywords & Rules, and Response Protocols.
 */
export function SafetyPage() {
  const { currentBotId } = useProject();
  const [activeTab, setActiveTab] = useState<TabType>('alerts');
  const [statusFilter, setStatusFilter] = useState<AlertStatus | ''>('');
  const [severityFilter, setSeverityFilter] = useState<AlertSeverity | ''>('');
  const [offset, setOffset] = useState(0);
  const [selectedAlertId, setSelectedAlertId] = useState<string | null>(null);
  const limit = 20;

  // Keyword categories state management
  const [keywordCategories, setKeywordCategories] = useState<KeywordCategory[]>(defaultKeywordCategories);
  const [activeInputCategory, setActiveInputCategory] = useState<number | null>(null);
  const [newKeyword, setNewKeyword] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Fetch current bot data to get the safety keywords
  const botQuery = trpc.bots.get.useQuery(
    { id: currentBotId! },
    { enabled: !!currentBotId }
  );

  // Mutation to update bot safety keywords
  const updateBotMutation = trpc.bots.update.useMutation({
    onSuccess: () => {
      setSaveSuccess(true);
      setIsSaving(false);
      // Clear success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    },
    onError: (error) => {
      setSaveError(error.message);
      setIsSaving(false);
      // Clear error message after 5 seconds
      setTimeout(() => setSaveError(null), 5000);
    },
  });

  // Initialize keyword categories from bot data when it loads
  useEffect(() => {
    if (botQuery.data) {
      const botData = botQuery.data;
      setKeywordCategories([
        {
          name: 'Crisis / Self-Harm',
          severity: 'high' as AlertSeverity,
          dbField: 'overrideSafetyKeywordsHigh',
          keywords: (botData.overrideSafetyKeywordsHigh as string[] | null) ?? [],
          protocol: 'crisis_immediate',
        },
        {
          name: 'Moderate Distress',
          severity: 'medium' as AlertSeverity,
          dbField: 'overrideSafetyKeywordsMedium',
          keywords: (botData.overrideSafetyKeywordsMedium as string[] | null) ?? [],
          protocol: 'frustration_standard',
        },
        {
          name: 'Negative Sentiment',
          severity: 'low' as AlertSeverity,
          dbField: 'overrideSafetyKeywordsLow',
          keywords: (botData.overrideSafetyKeywordsLow as string[] | null) ?? [],
          protocol: 'monitor_only',
        },
      ]);
    }
  }, [botQuery.data]);

  // Helper function to save keywords to the backend
  const saveKeywordsToBackend = (updatedCategories: KeywordCategory[]) => {
    if (!currentBotId) return;

    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    // Build the update payload
    const updatePayload: {
      id: string;
      overrideSafetyKeywordsHigh?: string[] | null;
      overrideSafetyKeywordsMedium?: string[] | null;
      overrideSafetyKeywordsLow?: string[] | null;
    } = { id: currentBotId };

    updatedCategories.forEach(cat => {
      // Set to null if empty, otherwise set the keywords array
      updatePayload[cat.dbField] = cat.keywords.length > 0 ? cat.keywords : null;
    });

    updateBotMutation.mutate(updatePayload);
  };

  // Handler to add a keyword to a category
  const handleAddKeyword = (categoryIndex: number) => {
    const trimmedKeyword = newKeyword.trim().toLowerCase();
    if (!trimmedKeyword) return;

    // Check if keyword already exists in this category
    if (keywordCategories[categoryIndex].keywords.includes(trimmedKeyword)) {
      setNewKeyword('');
      return;
    }

    const updatedCategories = [...keywordCategories];
    updatedCategories[categoryIndex] = {
      ...updatedCategories[categoryIndex],
      keywords: [...updatedCategories[categoryIndex].keywords, trimmedKeyword],
    };

    setKeywordCategories(updatedCategories);
    setNewKeyword('');
    setActiveInputCategory(null);

    // Save to backend
    saveKeywordsToBackend(updatedCategories);
  };

  // Handler to remove a keyword from a category
  const handleRemoveKeyword = (categoryIndex: number, keyword: string) => {
    const updatedCategories = [...keywordCategories];
    updatedCategories[categoryIndex] = {
      ...updatedCategories[categoryIndex],
      keywords: updatedCategories[categoryIndex].keywords.filter(kw => kw !== keyword),
    };

    setKeywordCategories(updatedCategories);

    // Save to backend
    saveKeywordsToBackend(updatedCategories);
  };

  // Handler to show the input field for adding a keyword
  const handleShowAddInput = (categoryIndex: number) => {
    setActiveInputCategory(categoryIndex);
    setNewKeyword('');
  };

  // Handler to cancel adding a keyword
  const handleCancelAddKeyword = () => {
    setActiveInputCategory(null);
    setNewKeyword('');
  };

  const alertsQuery = trpc.safety.alerts.useQuery(
    {
      status: statusFilter || undefined,
      severity: severityFilter || undefined,
      offset,
      limit,
      botId: currentBotId ?? undefined,
    },
    { enabled: !!currentBotId }
  );

  const statsQuery = trpc.safety.stats.useQuery(
    { botId: currentBotId ?? undefined },
    { enabled: !!currentBotId }
  );

  const utils = trpc.useUtils();

  const updateAlertMutation = trpc.safety.updateAlert.useMutation({
    onSuccess: () => {
      utils.safety.alerts.invalidate();
      utils.safety.stats.invalidate();
    },
  });

  const handleResolve = (alertId: string) => {
    if (confirm('Are you sure you want to resolve this alert?')) {
      updateAlertMutation.mutate({
        id: alertId,
        status: 'resolved',
        resolutionNotes: 'Reviewed and resolved by admin',
      });
    }
  };

  const handleFalsePositive = (alertId: string) => {
    updateAlertMutation.mutate({
      id: alertId,
      status: 'false_positive',
      resolutionNotes: 'Marked as false positive by admin',
    });
  };

  const tabs = [
    { id: 'alerts' as TabType, label: 'Alert Queue' },
    { id: 'keywords' as TabType, label: 'Keywords & Rules' },
    { id: 'protocols' as TabType, label: 'Response Protocols' },
  ];

  const filters: Array<{ id: AlertStatus | ''; label: string; count: number }> = [
    { id: '', label: 'All Alerts', count: alertsQuery.data?.pagination?.total ?? 0 },
    { id: 'needs_review', label: 'Needs Review', count: statsQuery.data?.byStatus?.needsReview ?? 0 },
    { id: 'in_progress', label: 'In Progress', count: statsQuery.data?.byStatus?.inProgress ?? 0 },
    { id: 'resolved', label: 'Resolved', count: statsQuery.data?.byStatus?.resolved ?? 0 },
  ];

  const needsReviewCount = statsQuery.data?.byStatus?.needsReview ?? 0;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Safety Monitor</h1>
          <p style={styles.subtitle}>Real-time safety detection & crisis intervention</p>
        </div>
        <div style={styles.headerActions}>
          {needsReviewCount > 0 && (
            <div style={styles.criticalBadge}>
              <div style={styles.pulseDot} />
              <span style={styles.criticalText}>
                {needsReviewCount} Needs Review
              </span>
            </div>
          )}
          <button style={styles.exportButton}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M7 1V13M1 7H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Export Report
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div style={styles.statsRow}>
        {[
          { label: 'Alerts Today', value: statsQuery.data?.byStatus?.needsReview ?? 0, color: '#C9A090', icon: '⚠️' },
          { label: 'Needs Review', value: needsReviewCount, color: needsReviewCount > 0 ? '#A04040' : '#5A8A5E', icon: '👁️', pulse: needsReviewCount > 0 },
          { label: 'In Progress', value: statsQuery.data?.byStatus?.inProgress ?? 0, color: '#9B7B3C', icon: '🔄' },
          { label: 'Resolved (7d)', value: statsQuery.data?.byStatus?.resolved ?? 0, color: '#5A8A5E', icon: '✓' },
          { label: 'Avg Response', value: '4m', color: '#5A6A8A', icon: '⏱️' },
        ].map((stat, i) => (
          <div key={i} style={styles.statCard}>
            <div style={styles.statHeader}>
              <span style={styles.statIcon}>{stat.icon}</span>
              {stat.pulse && (
                <div style={styles.pulseDotSmall} />
              )}
            </div>
            <p style={{ ...styles.statValue, color: stat.color }}>{stat.value}</p>
            <p style={styles.statLabel}>{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={styles.tabsContainer}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              ...styles.tab,
              ...(activeTab === tab.id ? styles.tabActive : {}),
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Alert Queue Tab */}
      {activeTab === 'alerts' && (
        <div style={styles.tabContent}>
          {/* Filters */}
          <div style={styles.alertFilters}>
            {filters.map(filter => (
              <button
                key={filter.id}
                onClick={() => {
                  setStatusFilter(filter.id);
                  setOffset(0);
                }}
                style={{
                  ...styles.filterButton,
                  ...(statusFilter === filter.id ? styles.filterButtonActive : {}),
                }}
              >
                {filter.label} ({filter.count})
              </button>
            ))}
          </div>

          {/* Severity Filter */}
          <div style={styles.severityFilters}>
            <select
              value={severityFilter}
              onChange={(e) => {
                setSeverityFilter(e.target.value as AlertSeverity | '');
                setOffset(0);
              }}
              style={styles.select}
            >
              <option value="">All Severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          {/* Alerts List */}
          {alertsQuery.isLoading ? (
            <div style={styles.loading}>Loading alerts...</div>
          ) : alertsQuery.error ? (
            <div style={styles.error}>
              Error loading alerts: {alertsQuery.error.message}
            </div>
          ) : alertsQuery.data?.alerts.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>🛡️</div>
              <h3 style={styles.emptyTitle}>No alerts found</h3>
              <p style={styles.emptyText}>
                {statusFilter || severityFilter
                  ? 'Try adjusting your filters'
                  : 'Great! No safety alerts to review.'}
              </p>
            </div>
          ) : (
            <>
              <div style={styles.alertsList}>
                {alertsQuery.data?.alerts.map((alert) => {
                  const severity = alert.severity as AlertSeverity;
                  const severityStyle = severityColors[severity] || severityColors.low;
                  const statusStyle = statusColors[alert.status] || statusColors.needs_review;
                  const category = (alert.keywordsMatched as string[])?.[0] || 'default';
                  const icon = categoryIcons[category] || categoryIcons.default;

                  return (
                    <div
                      key={alert.id}
                      style={{
                        ...styles.alertCard,
                        borderLeft: severity === 'critical' || severity === 'high'
                          ? `3px solid ${severityStyle.dot}`
                          : '3px solid transparent',
                        background: alert.status === 'needs_review'
                          ? 'rgba(180, 80, 70, 0.03)'
                          : '#ffffff',
                      }}
                    >
                      <div style={styles.alertCardInner}>
                        {/* Icon */}
                        <div
                          style={{
                            ...styles.alertIcon,
                            background: severityStyle.bg,
                            border: `1px solid ${severityStyle.border}`,
                          }}
                        >
                          {icon}
                        </div>

                        {/* Content */}
                        <div style={styles.alertContent}>
                          <div style={styles.alertTopRow}>
                            <div style={styles.alertBadges}>
                              <span style={styles.alertUser}>{alert.user?.name || 'Unknown User'}</span>
                              <span
                                style={{
                                  ...styles.severityBadge,
                                  background: severityStyle.bg,
                                  color: severityStyle.text,
                                }}
                              >
                                {severity.toUpperCase()}
                              </span>
                            </div>
                            <span style={styles.alertTime}>
                              {alert.createdAt ? new Date(alert.createdAt).toLocaleString() : 'Unknown'}
                            </span>
                          </div>

                          <p style={styles.alertMessage}>
                            "{alert.message?.content || 'No message content'}"
                          </p>

                          <div style={styles.alertMeta}>
                            <span
                              style={{
                                ...styles.statusBadge,
                                background: statusStyle.bg,
                                color: statusStyle.text,
                              }}
                            >
                              {statusStyle.label}
                            </span>
                            {alert.assignedAdmin && (
                              <span style={styles.assignedTo}>
                                → {alert.assignedAdmin.name}
                              </span>
                            )}
                            {Array.isArray(alert.keywordsMatched) && alert.keywordsMatched.length > 0 && (
                              <span style={styles.keywordTrigger}>
                                Trigger: "{alert.keywordsMatched.join(', ')}"
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {(alert.status === 'needs_review' || alert.status === 'in_progress') && (
                        <div style={styles.alertActions}>
                          <button
                            onClick={() => handleResolve(alert.id)}
                            disabled={updateAlertMutation.isPending}
                            style={styles.resolveButton}
                          >
                            ✓ {updateAlertMutation.isPending ? 'Resolving...' : 'Mark Resolved'}
                          </button>
                          <button
                            onClick={() => handleFalsePositive(alert.id)}
                            disabled={updateAlertMutation.isPending}
                            style={styles.falsePositiveButton}
                          >
                            ✗ {updateAlertMutation.isPending ? 'Updating...' : 'False Positive'}
                          </button>
                          <button
                            style={styles.viewButton}
                            onClick={() => setSelectedAlertId(alert.id)}
                          >
                            View Details
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {alertsQuery.data && alertsQuery.data.pagination.hasMore && (
                <div style={styles.pagination}>
                  <button
                    onClick={() => setOffset((o) => Math.max(0, o - limit))}
                    disabled={offset === 0}
                    style={styles.pageButton}
                  >
                    Previous
                  </button>
                  <span style={styles.pageInfo}>
                    Showing {offset + 1} - {Math.min(offset + limit, alertsQuery.data.pagination.total)} of {alertsQuery.data.pagination.total}
                  </span>
                  <button
                    onClick={() => setOffset((o) => o + limit)}
                    disabled={!alertsQuery.data.pagination.hasMore}
                    style={styles.pageButton}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Keywords & Rules Tab */}
      {activeTab === 'keywords' && (
        <div style={styles.keywordsContent}>
          <div style={styles.keywordsHeader}>
            <div>
              <h3 style={styles.sectionTitle}>Keyword Detection Rules</h3>
              <p style={styles.sectionDescription}>
                Configure keywords and phrases that trigger safety alerts
              </p>
            </div>
            <div style={styles.headerRight}>
              {/* Save status indicator */}
              {isSaving && (
                <span style={styles.savingIndicator}>
                  Saving...
                </span>
              )}
              {saveSuccess && (
                <span style={styles.successIndicator}>
                  ✓ Saved
                </span>
              )}
              {saveError && (
                <span style={styles.errorIndicator}>
                  Error: {saveError}
                </span>
              )}
            </div>
          </div>

          {/* Loading state */}
          {botQuery.isLoading && (
            <div style={styles.loading}>Loading keyword configuration...</div>
          )}

          {/* Error state */}
          {botQuery.error && (
            <div style={styles.error}>
              Error loading keywords: {botQuery.error.message}
            </div>
          )}

          {/* No bot selected state */}
          {!currentBotId && (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>🤖</div>
              <h3 style={styles.emptyTitle}>No Bot Selected</h3>
              <p style={styles.emptyText}>
                Please select a bot to configure safety keywords.
              </p>
            </div>
          )}

          {/* Keywords categories - show when bot data is loaded */}
          {currentBotId && !botQuery.isLoading && !botQuery.error && keywordCategories.map((cat, i) => {
            const severityStyle = severityColors[cat.severity] || severityColors.low;
            const protocol = protocols.find(p => p.id === cat.protocol);

            return (
              <div
                key={i}
                style={{
                  ...styles.keywordCategory,
                  border: `1px solid ${severityStyle.border}`,
                  background: severityStyle.bg,
                  opacity: isSaving ? 0.7 : 1,
                  pointerEvents: isSaving ? 'none' : 'auto',
                }}
              >
                <div style={styles.categoryHeader}>
                  <div style={styles.categoryInfo}>
                    <span
                      style={{
                        ...styles.categorySeverityBadge,
                        color: severityStyle.text,
                      }}
                    >
                      {cat.severity.toUpperCase()}
                    </span>
                    <h4 style={styles.categoryName}>{cat.name}</h4>
                  </div>
                  <span style={styles.categoryProtocol}>
                    Protocol: {protocol?.name}
                  </span>
                </div>
                <div style={styles.keywordsList}>
                  {cat.keywords.length === 0 && activeInputCategory !== i && (
                    <span style={styles.noKeywordsText}>No keywords configured</span>
                  )}
                  {cat.keywords.map(kw => (
                    <span key={kw} style={styles.keywordTag}>
                      {kw}
                      <button
                        style={styles.removeKeywordButton}
                        onClick={() => handleRemoveKeyword(i, kw)}
                        title="Remove keyword"
                        disabled={isSaving}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {activeInputCategory === i ? (
                    <div style={styles.addKeywordInputWrapper}>
                      <input
                        type="text"
                        value={newKeyword}
                        onChange={(e) => setNewKeyword(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleAddKeyword(i);
                          } else if (e.key === 'Escape') {
                            handleCancelAddKeyword();
                          }
                        }}
                        placeholder="Enter keyword..."
                        style={styles.addKeywordInput}
                        autoFocus
                        disabled={isSaving}
                      />
                      <button
                        style={styles.addKeywordConfirmButton}
                        onClick={() => handleAddKeyword(i)}
                        title="Add keyword"
                        disabled={isSaving}
                      >
                        ✓
                      </button>
                      <button
                        style={styles.addKeywordCancelButton}
                        onClick={handleCancelAddKeyword}
                        title="Cancel"
                        disabled={isSaving}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <button
                      style={styles.addKeywordButton}
                      onClick={() => handleShowAddInput(i)}
                      disabled={isSaving}
                    >
                      + Add keyword
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {currentBotId && !botQuery.isLoading && !botQuery.error && (
            <div style={styles.infoBox}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <circle cx="9" cy="9" r="8" stroke="#5A6A8A" strokeWidth="1.5"/>
                <path d="M9 5V9.5M9 13V13.01" stroke="#5A6A8A" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <p style={styles.infoText}>
                Sentiment analysis is also active and can trigger alerts based on message tone, even without keyword matches.
                Changes are saved automatically when you add or remove keywords.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Response Protocols Tab */}
      {activeTab === 'protocols' && (
        <div style={styles.protocolsContent}>
          <div style={styles.protocolsHeader}>
            <div>
              <h3 style={styles.sectionTitle}>Response Protocols</h3>
              <p style={styles.sectionDescription}>
                Define automated responses and escalation procedures
              </p>
            </div>
          </div>

          <div style={styles.protocolsGrid}>
            {protocols.map(protocol => (
              <div key={protocol.id} style={styles.protocolCard}>
                <div style={styles.protocolHeader}>
                  <div
                    style={{
                      ...styles.protocolDot,
                      background: protocol.color,
                    }}
                  />
                  <h4 style={styles.protocolName}>{protocol.name}</h4>
                </div>
                <p style={styles.protocolDescription}>{protocol.description}</p>
                <div style={styles.protocolActions}>
                  <button style={styles.protocolButton}>Edit Response</button>
                  <button style={styles.protocolButton}>Test</button>
                </div>
              </div>
            ))}
          </div>

          {/* Emergency Contact */}
          <div style={styles.emergencySection}>
            <h4 style={styles.emergencyTitle}>
              🚨 Emergency Escalation Contacts
            </h4>
            <div style={styles.emergencyGrid}>
              <div>
                <p style={styles.contactLabel}>Primary Contact</p>
                <p style={styles.contactName}>Sarah Johnson</p>
                <p style={styles.contactPhone}>+1 555-0199</p>
              </div>
              <div>
                <p style={styles.contactLabel}>Secondary Contact</p>
                <p style={styles.contactName}>Mike Chen</p>
                <p style={styles.contactPhone}>+1 555-0234</p>
              </div>
              <div>
                <p style={styles.contactLabel}>Crisis Hotline</p>
                <p style={styles.contactName}>988 Suicide & Crisis</p>
                <p style={styles.contactPhone}>Auto-sent to users</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pulse animation keyframes */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>

      {/* Alert Detail Modal */}
      {selectedAlertId && (
        <AlertDetail
          alertId={selectedAlertId}
          onClose={() => setSelectedAlertId(null)}
          onResolve={(alertId) => {
            handleResolve(alertId);
            setSelectedAlertId(null);
          }}
          isResolving={updateAlertMutation.isPending}
        />
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    maxWidth: '1200px',
    padding: '32px',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  title: {
    fontFamily: '"Sentient", Georgia, serif',
    fontSize: '24px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#9B9285',
    margin: 0,
  },
  headerActions: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  criticalBadge: {
    padding: '10px 16px',
    borderRadius: '10px',
    background: 'rgba(180, 80, 70, 0.15)',
    border: '1px solid rgba(180, 80, 70, 0.3)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  pulseDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#A04040',
    animation: 'pulse 2s infinite',
  },
  pulseDotSmall: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#A04040',
    animation: 'pulse 2s infinite',
  },
  criticalText: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#A04040',
  },
  exportButton: {
    padding: '10px 20px',
    borderRadius: '10px',
    background: '#3D3A36',
    border: 'none',
    color: 'white',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '18px',
    border: '1px solid rgba(93, 86, 78, 0.06)',
  },
  statHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
  statIcon: {
    fontSize: '18px',
  },
  statValue: {
    fontSize: '28px',
    fontWeight: '600',
    marginBottom: '2px',
    margin: 0,
  },
  statLabel: {
    fontSize: '12px',
    color: '#9B9285',
    margin: 0,
  },
  tabsContainer: {
    display: 'flex',
    gap: '4px',
    marginBottom: '20px',
  },
  tab: {
    padding: '10px 20px',
    borderRadius: '8px 8px 0 0',
    border: 'none',
    background: 'transparent',
    fontSize: '13px',
    fontWeight: '500',
    color: '#9B9285',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  tabActive: {
    background: 'white',
    fontWeight: '600',
    color: '#3D3A36',
  },
  tabContent: {
    background: 'white',
    borderRadius: '16px',
    border: '1px solid rgba(93, 86, 78, 0.06)',
    overflow: 'hidden',
    padding: '16px',
  },
  alertFilters: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    marginBottom: '16px',
    paddingBottom: '16px',
    borderBottom: '1px solid rgba(93, 86, 78, 0.06)',
  },
  filterButton: {
    padding: '6px 12px',
    borderRadius: '6px',
    border: '1px solid rgba(93, 86, 78, 0.1)',
    background: 'white',
    fontSize: '12px',
    fontWeight: '500',
    color: '#706A62',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  filterButtonActive: {
    border: '1px solid rgba(93, 86, 78, 0.3)',
    background: 'rgba(93, 86, 78, 0.08)',
    color: '#3D3A36',
  },
  severityFilters: {
    marginBottom: '16px',
  },
  select: {
    padding: '10px 16px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    outline: 'none',
  },
  loading: {
    padding: '40px',
    textAlign: 'center',
    color: '#64748b',
  },
  error: {
    padding: '16px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  emptyTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '8px',
  },
  emptyText: {
    fontSize: '14px',
    color: '#64748b',
  },
  alertsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
  },
  alertCard: {
    padding: '16px',
    borderBottom: '1px solid rgba(93, 86, 78, 0.06)',
    cursor: 'pointer',
  },
  alertCardInner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  alertIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    flexShrink: 0,
  },
  alertContent: {
    flex: 1,
    minWidth: 0,
  },
  alertTopRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '4px',
  },
  alertBadges: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  alertUser: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#3D3A36',
  },
  severityBadge: {
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  alertTime: {
    fontSize: '11px',
    color: '#9B9285',
  },
  alertMessage: {
    fontSize: '13px',
    color: '#5C564E',
    margin: '0 0 8px 0',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    lineHeight: '1.5',
  },
  alertMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  statusBadge: {
    padding: '3px 8px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  assignedTo: {
    fontSize: '11px',
    color: '#9B9285',
  },
  keywordTrigger: {
    fontSize: '11px',
    color: '#9B9285',
  },
  alertActions: {
    display: 'flex',
    gap: '8px',
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px solid rgba(93, 86, 78, 0.06)',
  },
  resolveButton: {
    padding: '8px 14px',
    borderRadius: '6px',
    background: 'rgba(152, 184, 154, 0.15)',
    border: '1px solid rgba(152, 184, 154, 0.3)',
    fontSize: '12px',
    fontWeight: '600',
    color: '#5A8A5E',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  viewButton: {
    padding: '8px 14px',
    borderRadius: '6px',
    background: 'white',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    fontSize: '12px',
    fontWeight: '500',
    color: '#5C564E',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  falsePositiveButton: {
    padding: '8px 14px',
    borderRadius: '6px',
    background: 'rgba(93, 86, 78, 0.08)',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    fontSize: '12px',
    fontWeight: '600',
    color: '#706A62',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    marginTop: '24px',
    padding: '16px',
    borderTop: '1px solid rgba(93, 86, 78, 0.06)',
  },
  pageButton: {
    padding: '8px 16px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    fontSize: '14px',
    cursor: 'pointer',
  },
  pageInfo: {
    fontSize: '14px',
    color: '#64748b',
  },
  // Keywords Tab Styles
  keywordsContent: {
    background: 'white',
    borderRadius: '16px',
    border: '1px solid rgba(93, 86, 78, 0.06)',
    padding: '24px',
    flex: 1,
    overflow: 'auto',
  },
  keywordsHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  savingIndicator: {
    fontSize: '13px',
    color: '#9B7B3C',
    fontWeight: '500',
  },
  successIndicator: {
    fontSize: '13px',
    color: '#5A8A5E',
    fontWeight: '500',
  },
  errorIndicator: {
    fontSize: '13px',
    color: '#A04040',
    fontWeight: '500',
  },
  noKeywordsText: {
    fontSize: '12px',
    color: '#9B9285',
    fontStyle: 'italic',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#3D3A36',
    margin: '0 0 4px 0',
  },
  sectionDescription: {
    fontSize: '13px',
    color: '#9B9285',
    margin: 0,
  },
  addCategoryButton: {
    padding: '10px 16px',
    borderRadius: '8px',
    background: '#3D3A36',
    border: 'none',
    fontSize: '13px',
    fontWeight: '600',
    color: 'white',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  keywordCategory: {
    padding: '20px',
    borderRadius: '12px',
    marginBottom: '16px',
  },
  categoryHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
  },
  categoryInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  categorySeverityBadge: {
    padding: '4px 10px',
    borderRadius: '6px',
    background: 'white',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  categoryName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#3D3A36',
    margin: 0,
  },
  categoryProtocol: {
    fontSize: '12px',
    color: '#9B9285',
  },
  keywordsList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  keywordTag: {
    padding: '6px 12px',
    borderRadius: '6px',
    background: 'white',
    fontSize: '12px',
    color: '#5C564E',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  removeKeywordButton: {
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    border: 'none',
    background: 'rgba(93, 86, 78, 0.1)',
    fontSize: '10px',
    color: '#9B9285',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addKeywordButton: {
    padding: '6px 12px',
    borderRadius: '6px',
    border: '1px dashed rgba(93, 86, 78, 0.3)',
    background: 'transparent',
    fontSize: '12px',
    color: '#9B9285',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  addKeywordInputWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  addKeywordInput: {
    padding: '6px 10px',
    borderRadius: '6px',
    border: '1px solid rgba(93, 86, 78, 0.3)',
    background: 'white',
    fontSize: '12px',
    color: '#3D3A36',
    outline: 'none',
    width: '120px',
    fontFamily: 'inherit',
  },
  addKeywordConfirmButton: {
    width: '24px',
    height: '24px',
    borderRadius: '4px',
    border: 'none',
    background: 'rgba(152, 184, 154, 0.2)',
    fontSize: '12px',
    color: '#5A8A5E',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addKeywordCancelButton: {
    width: '24px',
    height: '24px',
    borderRadius: '4px',
    border: 'none',
    background: 'rgba(180, 80, 70, 0.15)',
    fontSize: '12px',
    color: '#A04040',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoBox: {
    padding: '16px',
    borderRadius: '10px',
    background: 'rgba(90, 106, 138, 0.06)',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginTop: '8px',
  },
  infoText: {
    fontSize: '13px',
    color: '#5A6A8A',
    margin: 0,
  },
  // Protocols Tab Styles
  protocolsContent: {
    background: 'white',
    borderRadius: '16px',
    border: '1px solid rgba(93, 86, 78, 0.06)',
    padding: '24px',
    flex: 1,
    overflow: 'auto',
  },
  protocolsHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  protocolsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  protocolCard: {
    padding: '20px',
    borderRadius: '12px',
    border: '1px solid rgba(93, 86, 78, 0.1)',
    background: 'rgba(93, 86, 78, 0.02)',
  },
  protocolHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px',
  },
  protocolDot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
  },
  protocolName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#3D3A36',
    margin: 0,
  },
  protocolDescription: {
    fontSize: '13px',
    color: '#5C564E',
    marginBottom: '16px',
    lineHeight: '1.5',
  },
  protocolActions: {
    display: 'flex',
    gap: '8px',
  },
  protocolButton: {
    padding: '8px 14px',
    borderRadius: '6px',
    background: 'white',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    fontSize: '12px',
    fontWeight: '500',
    color: '#5C564E',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  emergencySection: {
    marginTop: '24px',
    padding: '20px',
    borderRadius: '12px',
    background: 'rgba(180, 80, 70, 0.05)',
    border: '1px solid rgba(180, 80, 70, 0.2)',
  },
  emergencyTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#A04040',
    margin: '0 0 12px 0',
  },
  emergencyGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '16px',
  },
  contactLabel: {
    fontSize: '11px',
    color: '#9B9285',
    marginBottom: '4px',
    margin: 0,
  },
  contactName: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#3D3A36',
    margin: 0,
  },
  contactPhone: {
    fontSize: '12px',
    color: '#5C564E',
    margin: 0,
  },
};

export default SafetyPage;
